class RichTextTruncator {
    
    constructor(selector, targetHeight) {
        this.container = document.querySelector(selector);
        this.targetHeightOption = targetHeight;
        // Pre-sort breakpoint keys for responsive heights
        this.sortedTargetBreakpoints = Array.isArray(this.targetHeightOption)
            ? []
            : Object.keys(this.targetHeightOption).map(Number).sort((a, b) => a - b);
        this.originalReadMoreBtn = this.container.querySelector('.read-more-button');
        this.descEl = this.container.querySelector('.collection-description');
        this.fullDesc = this.container.querySelector('.full-description');
        this.clonedReadMoreBtn = null;
        
        // Throttle utility
        this.throttle = (fn, limit = 200) => {
            let inThrottle = false;
            return (...args) => {
                if (!inThrottle) {
                    fn.apply(this, args);
                    inThrottle = true;
                    setTimeout(() => (inThrottle = false), limit);
                }
            };
        };
        // Bind throttled resize
        this.handleResizeThrottled = this.throttle(this.handleResize.bind(this), 200);
        
        // Track last window width to prevent no-op resizes
        this.lastWindowWidth = window.innerWidth;
        
        if (this.originalReadMoreBtn && this.container) {
            this.originalReadMoreBtn.style.display = 'none'; // hide original
            this.container.classList.add('loading'); // add loading state
            this.init();
        }
    }
    
    
    init() {
        // Helper to robustly wait for font paint
        const afterFontsPainted = (callback) => {
            document.fonts.ready.then(() => {
                // Wait extra paint cycles after fonts are loaded
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Give Safari extra breathing room
                        setTimeout(() => {
                            callback();
                        }, 50);
                    });
                });
            });
        };
        
        if (document.fonts) {
            afterFontsPainted(() => {
                this.adjustContainerHeight();
                // Re-check once more in case Safari "lied" on first paint
                setTimeout(() => {
                    this.adjustContainerHeight();
                }, 150);
            });
        } else {
            // Fallback for old browsers
            setTimeout(() => this.adjustContainerHeight(), 100);
        }
        
        window.addEventListener('resize', this.handleResizeThrottled, { passive: true });
    }
    
    
    createInlineReadMore() {
        if (this.clonedReadMoreBtn) this.clonedReadMoreBtn.remove();
        
        this.clonedReadMoreBtn = this.originalReadMoreBtn.cloneNode(true);
        this.clonedReadMoreBtn.style.display = 'inline-block';
        this.clonedReadMoreBtn.addEventListener('click', () => this.toggleDescription());
        this.descEl.appendChild(this.clonedReadMoreBtn);
    }
    
    
    toggleDescription() {
        if (!this.container.classList.contains('open')) {
            this.descEl.innerHTML = this.fullDesc.innerHTML;
            this.createInlineReadMore();
            this.clonedReadMoreBtn.innerHTML = this.clonedReadMoreBtn.dataset.readLessText;
            this.container.classList.add('open');
        } else {
            this.clonedReadMoreBtn.innerHTML = this.clonedReadMoreBtn.dataset.readMoreText;
            this.container.classList.remove('open');
            this.adjustContainerHeight();
        }
    }
    
    
    adjustContainerHeight() {
        const targetHeight = this.getResponsiveTargetHeight();
        let content = this.fullDesc.textContent;

        // Clear any previous truncation
        this.descEl.innerHTML = content;

        // Apply truncation with two-phase approach
        if (this.descEl.offsetHeight > targetHeight) {
            // Phase 1: truncate text until it fits without button
            this.descEl.innerHTML = content;
            while (this.descEl.offsetHeight > targetHeight) {
                content = content.slice(0, -3);
                this.descEl.innerHTML = content;
            }
            // Phase 2: append ellipsis and button, then further truncate if needed
            let truncated = content.trim() + "...";
            this.descEl.innerHTML = truncated;
            this.createInlineReadMore();
            // Further reduce if the button causes overflow
            while (this.descEl.offsetHeight > targetHeight) {
                content = content.slice(0, -3);
                truncated = content.trim() + "...";
                this.descEl.innerHTML = truncated;
                this.createInlineReadMore();
            }
        } else {
            // No truncation needed
            this.descEl.innerHTML = content;
            this.container.classList.remove('loading');
            this.descEl.style.visibility = 'visible';
            if (this.clonedReadMoreBtn) {
                this.clonedReadMoreBtn.remove();
                this.clonedReadMoreBtn = null;
            }
        }

        // Cleanup UI state in next animation frame
        requestAnimationFrame(() => {
            this.container.classList.remove('loading');
            this.descEl.style.visibility = 'visible';
            this.descEl.classList.remove('loading');
        });
    }
    
    
    handleResize() {
        const newWidth = window.innerWidth;
        if (newWidth === this.lastWindowWidth) return;
        this.lastWindowWidth = newWidth;
        this.container.classList.add('loading');
        this.descEl.style.visibility = 'hidden';
        if (this.clonedReadMoreBtn) this.clonedReadMoreBtn.style.display = 'none';
        this.adjustContainerHeight();
    }
    
    
    getResponsiveTargetHeight() {
        if (typeof this.targetHeightOption === 'number') {
            return this.targetHeightOption;
        }
        let height = 30;
        for (const bp of this.sortedTargetBreakpoints) {
            if (window.innerWidth >= bp) {
                height = this.targetHeightOption[bp];
            }
        }
        return height;
    }
    
    
}

// Usage - Currently only powers the collection grid
document.addEventListener('DOMContentLoaded', () => {
    new RichTextTruncator('.collection-grid .collection-description-container', {
        0: 40,  // For mobile (0px - 768px) use 40px height
        768: 30, // For desktop (768px and above) use 30px height
    });
});